import React from 'react';
import { DayPilot, DayPilotScheduler } from "daypilot-pro-react";
import Zoom from '../../calendar/Zoom';
import Navigation from '../../calendar/Navigation';
import TaskList from './TaskList';
import TaskService from '../../../services/TaskService';

import '../../calendar/styles.css';
import './schedulerview.css';

class SchedulerView extends React.Component {

  constructor(props) {
    super(props);

    this.onBeforeRowHeaderRender = this.onBeforeRowHeaderRender.bind(this);
    this.onBeforeResHeaderRender = this.onBeforeResHeaderRender.bind(this);
    this.onBeforeCellRender = this.onBeforeCellRender.bind(this);
    this.onBeforeEventRender = this.onBeforeEventRender.bind(this);
    this.onEventMoved = this.onEventMoved.bind(this);
    this.onEventMove = this.onEventMove.bind(this);

    this.zoomChange = this.zoomChange.bind(this);
    this.periodChange = this.periodChange.bind(this);
    this.taskSelect = this.taskSelect.bind(this);

    this.state = {
      locale: "en-us",
      startDate: new DayPilot.Date().firstDayOfWeek(),
      selectedViewType: "Week",
      eventMovingStartEndEnabled: false,
      eventResizingStartEndEnabled: false,
      timeRangeSelectingStartEndEnabled: false,
      allowEventOverlap: true,
      cellWidthSpec: "Auto",
      rowMinHeight: 60,
      eventHeight: 55,
      timeFormat: "Clock12Hours",
      weekStarts: 0,
      businessBeginsHour: 6,
      businessEndsHour: 20,
      businessWeekends: true,
      showNonBusiness: true,
      days: 7,
      scale: "Day",
      timeHeaders: [
        { groupBy: "Month" },
        { groupBy: "Day", format: "ddd d" }
      ],
      resources: [],
      events: [],
      tasks: []
    };
  }

  static getDerivedStateFromProps(props, state) {

    if (props.resources !== state.resources) {
      return {
        resources: props.resources
      }
    }

    return null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resources !== this.props.resources) {
      this.loadEvents();
    }
  }

  componentDidMount() {
    this.loadTasks(true);
  }

  loadTasks(mounting = false) {
    return TaskService
      .getTasks({ projectId: this.props.projectId, category: this.props.sectionId })
      .then(result => {

        let startDate = new DayPilot.Date().firstDayOfWeek();
        const tasks = result.items.reduce((list, task) => {

          const parsedTask = this.parseTaskResponse(task);

          if (list.length === 0) {
            startDate = parsedTask.start.firstDayOfWeek();
          }

          list.push(parsedTask);
          return list;
        }, []);

        if (mounting) {
          return { tasks, startDate };
        }

        return { tasks };
      })
      .then(response =>
        this.setState({ ...response })
      );
  }

  loadEvents() {

    const start = this.state.startDate.toString();
    const end = this.state.startDate.addDays(this.state.days).toString();
    const userIds = this.state.resources.reduce((idArray, resource) => {
      idArray.push(resource.id);
      return idArray;
    }, []);

    const requestParams = {
      start: start,
      end: end,
      users: [...userIds]
    };

    TaskService.getTasks(requestParams)
      .then(result => {

        const events = result.items.reduce((list, task) => {
          list.push(this.parseTaskResponse(task));
          return list;
        }, []);

        return events;
      })
      .then(response =>
        this.setState({
          events: [...this.state.tasks.filter(e => e.resource === -999), ...response]
        }));
  }

  parseTaskResponse(taskResource) {

    const task = { id: taskResource.id, ...taskResource.attributes };

    return {
      id: task.id,
      start: new DayPilot.Date(task.startTime),
      end: new DayPilot.Date(task.endTime),
      title: task.title,
      text: task.description,
      resource: task.user ? task.user.id : -999,
      resourceName: task.user ? task.user.name : 'Unassigned',
      categoryId: task.catagoryId,
      projectId: task.projectId,
      allDay: task.allDay,
      moveVDisabled: task.projectId !== this.props.projectId,
      moveHDisabled: true
    }
  }

  updateEvents(startDate) {
    const start = this.state.days === 1 ? startDate : startDate.firstDayOfWeek();
    if (this.state.startDate !== start) {
      this.setState({ startDate: start }, this.loadEvents);
    }
  }

  taskSelect(task) {
    this.updateEvents(task.start);
  }

  periodChange(args) {
    const newStart = this.scheduler.startDate.addDays(args.direction * this.state.days)
    this.updateEvents(newStart);
  }

  zoomChange(args) {

    switch (args.level) {
      case "Day":
        this.setState({
          selectedViewType: args.level,
          days: 1,
          scale: "Hour",
          timeHeaders: [
            { groupBy: "Day", format: "dddd, d MMMM yyyy" },
            { groupBy: "Hour" }
          ]
        });
        break;
      case "Week":
        this.setState({
          startDate: this.state.startDate.firstDayOfWeek(),
          selectedViewType: args.level,
          days: 7,
          scale: "Day",
          timeHeaders: [
            { groupBy: "Month" },
            { groupBy: "Day", format: "ddd d" }
          ]
        });
        break;
      default:
        throw new Error("Invalid zoom level");
    }
  }

  onBeforeCellRender(args) {
    if (args.cell.resource === -999) {
      args.cell.backColor = "#ffe599";
    }
  }

  onBeforeRowHeaderRender(args) {

    if (args.row.id === -999) {
      args.row.backColor = "#ffe599";
    }

    const duration = args.row.events.totalDuration();
    if (duration.ticks > 0) {

      const totalHours = duration.totalHours().toFixed(2);
      args.row.html += " (" + totalHours + "h)";

      if (totalHours > args.row.data.maxHours) {
        args.row.backColor = "#ff0000";
        args.row.fontColor = "#f5f6f8";
      }
    }
  }

  onBeforeResHeaderRender(args) {
    const resTitle = args.resource.id === -999 ? '' : `${args.resource.role}<br />${args.resource.maxHours}h`;
    args.resource.html = `<strong>${args.resource.name}</strong><br />${resTitle}`;
  }

  onBeforeEventRender(args) {

    const startTime = args.e.start.toString('hh:mm');
    const endTime = args.e.end.toString('hh:mm');
    const timeRange = args.e.allDay ? "All day" : `${startTime}-${endTime}`;

    if (args.e.resource === -999) {
      args.e.backColor = "#ff6600";
    }

    args.e.html = `<strong>${args.e.title}</strong><br />${args.e.text}<br />${timeRange}`;
    args.e.bubbleHtml = `<strong>${args.e.text}</strong><br />${timeRange}`;
  }

  onEventMoved(args) {

    const { ...data } = args.e.data;
    const task = {
      id: data.id,
      attributes: {
        startTime: data.start,
        endTime: data.end,
        user: {
          id: data.resource === -999 ? 0 : data.resource
        }
      }
    };

    return TaskService
      .updateTask(task)
      .then(() => this.loadTasks());
  }

  onEventMove(args) {
    if (args.e.data.projectId !== this.props.projectId) {
      args.preventDefault();
    }
  }

  render() {

    const hasTasks = this.state.tasks && this.state.tasks.length;

    return (hasTasks ?
      <div className="scheduler-container">
        <div className="left-column">
          <div className="header">
            <Zoom
              selected={this.state.selectedViewType}
              onChange={this.zoomChange} />
            <Navigation
              onChange={this.periodChange} />
          </div>
          <DayPilotScheduler
            ref={component => { this.scheduler = component && component.control; }}
            {...this.state}
            onBeforeRowHeaderRender={this.onBeforeRowHeaderRender}
            onBeforeResHeaderRender={this.onBeforeResHeaderRender}
            onBeforeCellRender={this.onBeforeCellRender}
            onBeforeEventRender={this.onBeforeEventRender}
            onEventMoved={this.onEventMoved}
            onEventMove={this.onEventMove} />
        </div>
        <div className="right-column">
          <div className="title">Tasks</div>
          <div className="task-list">
            <TaskList
              tasks={this.state.tasks}
              onSelect={this.taskSelect} />
          </div>
        </div>
      </div> : "No tasks defined for the project"
    );
  }
}

export default SchedulerView;



// WEBPACK FOOTER //
// ./src/components/projects/ProjectSchedule/SchedulerView.js